// @ts-expect-error
import {browserHistory, includeLocation} from '@pb/kit-history';

import {addHash} from './addHash';
import {scrollToDOMNodeBySelector} from './smoothScroll';

import type {Options} from './smoothScroll';
import type {Location} from 'history';


type Elements = Array<[Element | null | undefined, boolean]>;

const HALF_SECOND = 500;
const waitForPageLoadInIntervalTimes = 6;

class ScrollSpy {
    callback: () => unknown;
    _on: boolean;
    _body: HTMLElement;
    _prevInnerWidth: number | null | undefined;
    elements: Map<Element | null | undefined, boolean>;
    prepareElements: Array<string>;
    busy: boolean;
    pageHeight: number;
    mainLocation: string;
    selectedTab: string;
    hashChangedInternally: boolean;
    jump: boolean;
    lateScrollInterval: ReturnType<typeof setInterval>;
    scrollingTimer: ReturnType<typeof setTimeout>;
    scrollToAfterResizeTimer: ReturnType<typeof setTimeout>;
    scrollOnVerticalResize: boolean;

    constructor() {
        this._on = false;
        this.hashChangedInternally = false;
        this.jump = false;
        this.prepareElements = [];
        this.callback = () => undefined;
        this.scrollOnVerticalResize = true;
        this._prevInnerWidth = window.innerWidth;

        if (document.body) {
            this._body = document.body;
        }

        let currentLocation: Location = browserHistory.location;
        browserHistory.listen((newLocation: Location) => {
            const location: Location = newLocation.pathname
                ? newLocation
                // @ts-expect-error
                : newLocation.location;
            const {pathname, hash} = location;

            const clearPathname = currentLocation.pathname.replace(/\/$/, '');

            if (this._on
                && !this.hashChangedInternally
                && clearPathname === pathname
                && hash
                && currentLocation.hash !== hash) {
                const upcomingSubstring = hash.substring(1);
                this.scrollTo(upcomingSubstring);
                this.setSelectedTab(upcomingSubstring);
            }
            currentLocation = location;
            this.hashChangedInternally = false;
        });
    }

    static prepareElements = (
        elements?: Array<string>,
    ): Elements | undefined => (
        elements
            ? elements.map(item => [
                document.querySelector(`#${item.replace('/', '\\/')}`),
                true,
            ]) as Elements
            : undefined
    );

    static isInViewport = (elem?: Element | null) => {
        if (elem) {
            const distance = elem.getBoundingClientRect();
            const height = window.innerHeight;

            return (
                distance.top >= 0 && distance.bottom <= height ||
                distance.top <= 0 && distance.bottom >= height ||
                distance.top <= height / 2 && distance.bottom >= height / 2
            );
        }

        return false;
    };

    static getSelectedTabName = (elements: Map<Element | null | undefined, boolean>): Promise<string> => {
        return new Promise(resolve => {
            for (const [key, value] of elements) {
                if (value && key && key.id) {
                    resolve(key.id);
                    return;
                }
            }
        });
    };

    setElementsToWatch = (elements: Array<string>) => {
        this.prepareElements = [...this.prepareElements, ...elements];
        this.elements = new Map(
            ScrollSpy.prepareElements(this.prepareElements),
        );
        this.updateElements();
        this.pageHeight = this._body.scrollHeight;
    };

    setMainLocation = (location: string) => {
        this.mainLocation = location;
    };
    setCallback = (func: () => unknown) => {
        this.callback = func;
    };

    on = () => {
        if (this.elements) {
            window.addEventListener('touchmove', this.spyScroll);
            window.addEventListener('scroll', this.spyScroll, {passive: true});
            window.addEventListener('resize', this.resize);
            window.addEventListener('click', this.interruptScroll);
            this._on = true;
            this.jump = true;
            this._body.scrollTop = 0;
        }

        return this;
    };

    off = () => {
        window.removeEventListener('touchmove', this.spyScroll);
        window.removeEventListener('scroll', this.spyScroll);
        window.removeEventListener('resize', this.resize);
        window.removeEventListener('click', this.interruptScroll);
        this._on = false;
        this.jump = false;

        this.callback = () => undefined;
    };

    scrollStart = () => {
        this.busy = true;
    };

    scrollStop = () => {
        this.busy = false;
        this.jump = false;
        this.callback();
    };

    clear = () => {
        this.elements = new Map();
        this.mainLocation = '';
        this.selectedTab = '';
        this.prepareElements = [];
        this.scrollOnVerticalResize = true;
        this._prevInnerWidth = window.innerWidth;
        clearInterval(this.lateScrollInterval);
        return this;
    };

    lateScroll = (selectedElement: string) => {
        let counter = 0;
        this.lateScrollInterval = setInterval(() => {
            counter++;
            if (counter > waitForPageLoadInIntervalTimes) {
                clearInterval(this.lateScrollInterval);
            }
            if (this.pageHeight !== this._body.scrollHeight) {
                this.jump = true;
                this.pageHeight = this._body.scrollHeight;
                this.scrollTo(selectedElement, {behavior: 'instant'});
            }
        }, HALF_SECOND);
    };

    scrollTo = (selectedElement: string, options?: Options) => {
        if (selectedElement) {
            this.scrollStart();
            scrollToDOMNodeBySelector(`#${selectedElement}`, options)
                .then(() => {
                    this.scrollStop();
                });
        }

        return this;
    };

    interruptScroll = () => {
        clearInterval(this.lateScrollInterval);
    };

    spyScroll = () => {
        clearTimeout(this.scrollingTimer);

        if (this.busy || this.mainLocation && !includeLocation(this.mainLocation)) {
            return;
        }

        if (this.jump) {
            this.jump = false;
            return;
        }

        this.updateElements();
        this.getSelectedTab().then(this.setSelectedTab);

        this.scrollingTimer = setTimeout(() => {
            this.getSelectedTab().then(this.replaceHash);
        }, 50);
    };

    resize = () => {
        if (this.selectedTab) {
            const noHorizontalResize = this._prevInnerWidth === window.innerWidth;
            const preventScrollOnVerticalResize = !this.scrollOnVerticalResize && noHorizontalResize;

            if (preventScrollOnVerticalResize) {
                return;
            }

            this._prevInnerWidth = window.innerWidth;

            clearInterval(this.lateScrollInterval);
            clearTimeout(this.scrollToAfterResizeTimer);
            this.scrollToAfterResizeTimer = setTimeout(() => {
                this.scrollTo(this.selectedTab);
            }, 100);
        }
    };

    setSelectedTab = (selectedElement: string): ScrollSpy => {
        this.selectedTab = selectedElement;
        this.callback();
        return this;
    };

    getSelectedTab = (): Promise<string> => {
        return ScrollSpy.getSelectedTabName(this.elements);
    };

    replaceHash = () => {
        this.addActiveHash(this.selectedTab);
    };

    jumpToSection = (selectedElement: string): ScrollSpy => {
        this.addActiveHash(selectedElement);
        this.jump = true;
        this
            .setSelectedTab(selectedElement)
            .scrollTo(selectedElement);
        return this;
    };

    updateElements = () => {
        this.elements.forEach((value, key, elements) => {
            elements.set(key, ScrollSpy.isInViewport(key));
        });
    };

    addActiveHash = (hash: string) => {
        if (browserHistory.location.hash.substring(1) !== hash) {
            this.hashChangedInternally = true;
            addHash(hash);
        }
    };
}

export const scrollSpy: ScrollSpy = new ScrollSpy();
